import { router } from "@inertiajs/react";
import type { SortingState } from "@tanstack/react-table";

import type { PagyMetadata, RansackFilters } from "@/types";

interface UseDataTableProps {
  filters: RansackFilters;
  pagy: PagyMetadata;
  /**
   * Optional explicit path for navigation. Defaults to current pathname.
   * Prefer js-routes helpers in pages and pass them here for clarity.
   */
  path?: string;
}

interface UseDataTableReturn {
  isLoading: boolean;
  handleFilterChange: (newFilters: Partial<RansackFilters>) => void;
  handlePageChange: (page: number) => void;
  handleSortingChange: (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState),
  ) => void;
  getSortingState: () => SortingState;
}

/**
 * Minimalist hook for server-side data table operations.
 *
 * Philosophy:
 * - No local cache: URL is the source of truth; backend returns data.
 * - Guardrails: do not navigate if nothing changed (prevents refresh loops).
 * - Preserve local UI state (e.g., selections) across visits of the same page.
 *
 * Tips:
 * - Do NOT call the handlers inside effects on every render — only on real user
 *   actions (typing/debounced input, clicking pagination, sorting). Otherwise
 *   you can cause unwanted page resets (e.g., forcing `page=1`).
 * - Avoid passing unstable objects each render; this hook already cleans and
 *   compares filter values before navigating.
 */
export function useDataTable({
  filters,
  pagy,
  path,
}: UseDataTableProps): UseDataTableReturn {
  // No internal state! Loading is handled by Inertia's progress indicator
  const isLoading = false;

  // Clean undefined/empty filters from object (keeps URL tidy and comparisons stable)
  const cleanFilters = (obj: Partial<RansackFilters>): Partial<RansackFilters> => {
    const result: Record<string, string> = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        // Normalize to string for URL params
        result[k] = String(v);
      }
    });
    return result as Partial<RansackFilters>;
  };

  // Shallow equality between two Ransack filter objects
  const filtersEqual = (
    a: Partial<RansackFilters>,
    b: Partial<RansackFilters>,
  ): boolean => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (a[key as keyof RansackFilters] !== b[key as keyof RansackFilters]) {
        return false;
      }
    }
    return true;
  };

  /**
   * Change filters - just update the URL, server handles everything
   */
  const handleFilterChange = (newFilters: Partial<RansackFilters>) => {
    // Merge new values over current server-provided filters
    const merged = { ...filters, ...newFilters };
    const cleanedNext = cleanFilters(merged);
    const cleanedCurrent = cleanFilters(filters);

    // If nothing actually changed, do nothing (prevents unwanted page=1 loops)
    if (filtersEqual(cleanedNext, cleanedCurrent)) return;

    // Reset to page 1 when filters change
    router.get(
      path ?? window.location.pathname,
      { q: cleanedNext, page: 1 },
      {
        // Keep local component state (e.g., selection) when the component is the same
        preserveState: true,
        preserveScroll: true,
      },
    );
  };

  /**
   * Change page - preserve current filters
   */
  const handlePageChange = (page: number) => {
    // If the requested page is falsy or identical to current, skip
    if (!page || page === pagy.page) return;

    router.get(
      path ?? window.location.pathname,
      { q: cleanFilters(filters), page },
      {
        preserveState: true,
        preserveScroll: true,
      },
    );
  };

  /**
   * Convert Ransack sort to TanStack format
   */
  const getSortingState = (): SortingState => {
    if (!filters.s) return [];

    const parts = filters.s.split("_");
    const direction = parts.pop();
    const column = parts.join("_");

    return [
      {
        id: column,
        desc: direction === "desc",
      },
    ];
  };

  /**
   * Change sorting
   */
  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const sorting =
      typeof updaterOrValue === "function"
        ? updaterOrValue(getSortingState())
        : updaterOrValue;

    if (sorting.length > 0) {
      const sort = sorting[0];
      const sortParam = `${sort.id}_${sort.desc ? "desc" : "asc"}`;
      // Avoid triggering when sort is already applied
      if (filters.s !== sortParam) {
        handleFilterChange({ s: sortParam });
      }
    } else {
      if (filters.s !== undefined) {
        handleFilterChange({ s: undefined });
      }
    }
  };

  return {
    isLoading,
    handleFilterChange,
    handlePageChange,
    handleSortingChange,
    getSortingState,
  };
}
